import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFamily } from '../lib/family.jsx';
import { BackLink } from '../ui/Nav.jsx';
import { ageAt, MONTH_NAMES, nextRaiseDate } from '../domain/age.js';
import { toDateOnly } from '../domain/periods.js';
import ChildForm from '../ui/ChildForm.jsx';

export default function Children() {
  const { kids, addChild, updateChild, archiveChild } = useFamily();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  // Two taps to remove, because there is no undo for it on this screen.
  const [confirmRemove, setConfirmRemove] = useState(false);
  const today = toDateOnly(new Date());

  function stopEditing() { setEditingId(null); setConfirmRemove(false); }

  async function run(fn) {
    setBusy(true); setError(null);
    try { await fn(); return true; } catch (e) { setError(e.message); return false; } finally { setBusy(false); }
  }

  const add = (values) => run(() => addChild(values));

  async function save(id, values) {
    if (await run(() => updateChild(id, values))) stopEditing();
  }

  async function remove(id) {
    if (!confirmRemove) { setConfirmRemove(true); return; }
    if (await run(() => archiveChild(id))) stopEditing();
  }

  return (
    <main className="app-shell stack">
      <div className="stack tight">
        <BackLink to="/family">Family</BackLink>
        <h1>The crew</h1>
      </div>

      {error && <p className="notice error">{error}</p>}

      {kids.length > 0 && (
        <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {kids.map((k) => {
            const age = ageAt(today, { birthMonth: k.birth_month, birthYear: k.birth_year });
            const raise = nextRaiseDate(today, { birthMonth: k.birth_month });

            if (k.id === editingId) {
              return (
                <li key={k.id} className="stack tight">
                  <p className="eyebrow">Editing {k.display_name}</p>
                  <ChildForm
                    initial={k}
                    busy={busy}
                    onSubmit={(values) => save(k.id, values)}
                    onCancel={stopEditing}
                  />
                  <div className="card stack tight">
                    <p className="small muted">
                      Removing {k.display_name} takes them off the crew list. Everything they
                      earned and spent stays in the history.
                    </p>
                    <div className="row">
                      <button type="button" onClick={() => remove(k.id)} disabled={busy}>
                        {confirmRemove ? 'Really remove? Tap again' : 'Remove from the crew'}
                      </button>
                      {confirmRemove && (
                        <button className="quiet" type="button" onClick={() => setConfirmRemove(false)}>
                          Keep them
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            }

            return (
              <li key={k.id} className="card spread">
                <div className="stack tight">
                  <strong>{k.display_name}</strong>
                  <span className="small muted">
                    {age} · born {MONTH_NAMES[k.birth_month - 1]} {k.birth_year}
                    {k.pin_hash ? ' · PIN set' : ' · no PIN'}
                  </span>
                </div>
                <div className="row">
                  <span className="small muted">next raise {raise.slice(0, 7)}</span>
                  <button
                    type="button"
                    onClick={() => { setEditingId(k.id); setConfirmRemove(false); }}
                  >
                    Edit<span className="visually-hidden"> {k.display_name}</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <h2>Add a child</h2>
      <ChildForm onSubmit={add} busy={busy} />

      <Link to="/invite" className="small">Invite another parent</Link>
    </main>
  );
}
